import { Link } from "@tanstack/react-router";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export function PrivacyPolicyPage() {
  useDocumentTitle("Privacy policy");
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link
          to="/login"
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          ← Back
        </Link>

        <header className="mt-6 space-y-1">
          <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
            Boo Lab, Inc.
          </p>
          <h1 className="text-3xl font-semibold">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Effective Date: April 29, 2026</p>
        </header>

        <div className="mt-8 space-y-4 text-sm leading-6 text-foreground">
          <p>
            Boo Lab, Inc. provides AI Enterprise Agents, an enterprise software platform that
            helps business customers build, deploy, manage, and connect AI agents across business
            systems, applications, communications tools, data sources, and workflows.
          </p>
          <p>
            This Privacy Policy explains how Boo Lab, Inc. collects, uses, discloses, and
            protects personal information in connection with AI Enterprise Agents, our website at{" "}
            <a
              href="https://boolab.ai"
              className="underline underline-offset-4 hover:text-foreground"
            >
              https://boolab.ai
            </a>
            , customer accounts, product usage, support, sales, billing, integrations, and
            related services.
          </p>
          <p>
            This Privacy Policy is intended for business customers and business users. AI
            Enterprise Agents is not intended for personal, household, or consumer use.
          </p>
          <p>
            For privacy questions, contact us at{" "}
            <a
              href="mailto:privacy@boolab.ai"
              className="underline underline-offset-4 hover:text-foreground"
            >
              privacy@boolab.ai
            </a>
            .
          </p>

          <h2 className="mt-10 text-lg font-semibold">1. Who We Are</h2>
          <p>Boo Lab, Inc. is located at:</p>
          <p>
            1515 E Cesar Chavez St, Suite 100
            <br />
            Austin, TX 78702
            <br />
            USA
          </p>
          <p>
            In this Privacy Policy, "Boo Lab," "we," "us," and "our" refer to Boo Lab, Inc.
          </p>
          <p>
            "Services" means AI Enterprise Agents, https://boolab.ai, and any related products,
            software, APIs, agents, integrations, support, documentation, and services provided
            by Boo Lab.
          </p>
          <p>
            "Customer" means the business or organization that purchases, subscribes to,
            evaluates, or uses the Services.
          </p>
          <p>"User" means an individual authorized by a Customer to access or use the Services.</p>
          <p>
            "Customer Content" means data, prompts, instructions, files, messages, records,
            outputs, workflows, metadata, or other content submitted to, processed by, or
            generated through the Services on behalf of a Customer.
          </p>

          <h2 className="mt-10 text-lg font-semibold">2. Information We Collect</h2>
          <p>We collect information in the following ways.</p>

          <h3 className="mt-6 font-semibold">Account and Business Contact Information</h3>
          <p>
            We collect information such as name, business email address, company name, job
            title, phone number, billing information, account credentials, user role, workspace
            information, and related business contact details.
          </p>

          <h3 className="mt-6 font-semibold">Product Usage and Technical Information</h3>
          <p>
            We collect information about how Users access and use the Services, including log
            data, device and browser information, IP address, authentication events, feature
            usage, agent activity logs, API usage, error logs, security logs, performance data,
            and telemetry.
          </p>

          <h3 className="mt-6 font-semibold">Customer Content</h3>
          <p>
            We process Customer Content submitted to or generated through AI Enterprise Agents.
            Customer Content may include business documents, prompts, agent instructions, files,
            database records, messages, emails, tickets, calendar events, CRM records, source
            code, issues, tasks, chat messages, knowledge base content, transcripts, summaries,
            agent outputs, workflow steps, and data from connected systems.
          </p>
          <p>
            Customers control the Customer Content they submit to the Services and are
            responsible for ensuring they have the rights and permissions needed to provide that
            content to Boo Lab.
          </p>

          <h3 className="mt-6 font-semibold">Integration Data</h3>
          <p>
            Customers may connect AI Enterprise Agents to third party systems and services.
            Depending on Customer configuration, we may process data from integrations including
            Slack, Microsoft Teams, Discord, Gmail, Google Calendar, Google Drive, Google Docs,
            Google Sheets, Google Slides, GitHub, GitLab, Bitbucket, Jira, Confluence, Notion,
            Linear, Asana, Trello, Salesforce, HubSpot, Zendesk, Intercom, Stripe, Airtable, Box,
            Dropbox, OneDrive, SharePoint, Zoom, Microsoft Outlook, Microsoft Exchange, AWS,
            Azure, Google Cloud, Datadog, Snowflake, BigQuery, PostgreSQL, MySQL, MongoDB, Redis,
            Elasticsearch, Segment, Amplitude, Mixpanel, Zapier, Make, Workato, Okta, Auth0, and
            other business systems selected by Customers.
          </p>
          <p>
            The specific information processed from each integration depends on the permissions
            granted by the Customer and the configuration of the Services.
          </p>

          <h3 className="mt-6 font-semibold">Communications and Support Information</h3>
          <p>
            We collect information when Customers or Users communicate with us, including
            support requests, sales inquiries, feedback, product questions, meeting notes, call
            recordings where disclosed, chat messages, and related correspondence.
          </p>

          <h3 className="mt-6 font-semibold">Website Information</h3>
          <p>
            When visitors use https://boolab.ai, we may collect information such as IP address,
            browser type, referring pages, pages viewed, interactions with the website, and
            cookie or similar technology data.
          </p>

          <h2 className="mt-10 text-lg font-semibold">3. How We Use Information</h2>
          <p>We use information to:</p>
          <ol className="list-decimal space-y-1 pl-6">
            <li>provide, operate, secure, and improve the Services;</li>
            <li>create and manage Customer accounts and Users;</li>
            <li>authenticate Users and prevent unauthorized access;</li>
            <li>process Customer Content and operate AI agents as configured by Customers;</li>
            <li>connect to and operate Customer selected integrations;</li>
            <li>
              generate, summarize, classify, transform, retrieve, route, or act on Customer
              Content as directed by Customers or Users;
            </li>
            <li>monitor performance, reliability, usage, and availability;</li>
            <li>
              detect, prevent, and respond to security incidents, abuse, fraud, policy
              violations, and unlawful activity;
            </li>
            <li>provide customer support and respond to requests;</li>
            <li>
              communicate about product updates, security notices, administrative messages, and
              service related matters;
            </li>
            <li>manage billing, contracts, compliance, and business operations;</li>
            <li>
              develop, test, evaluate, and improve Boo Lab products, services, models, systems,
              and features, subject to Customer controls and opt out rights described below;
            </li>
            <li>comply with legal obligations and enforce our agreements.</li>
          </ol>

          <h2 className="mt-10 text-lg font-semibold">
            4. Use of Customer Content for AI Improvement
          </h2>
          <p>
            Unless a Customer opts out, Boo Lab may use Customer Content, prompts, agent
            interactions, outputs, telemetry, feedback, and usage data to develop, evaluate,
            train, fine tune, test, secure, and improve Boo Lab models, agents, products, and
            services.
          </p>
          <p>
            Customers may opt out of broader Boo Lab model improvement by contacting
            privacy@boolab.ai or through any product controls Boo Lab makes available. After an
            opt out is processed, Boo Lab will not use that Customer's Customer Content for
            broader Boo Lab model training or improvement, except where needed to provide the
            Services, maintain security, debug issues, comply with law, enforce agreements, or as
            otherwise permitted by the Customer.
          </p>
          <p>
            Boo Lab may continue to use aggregated, deidentified, anonymized, or statistical
            information to understand usage, improve reliability, enhance safety, measure
            performance, and develop products, provided such information does not identify a
            Customer or User.
          </p>
          <p>
            Customer specific configuration, evaluation, retrieval, memory, or improvement
            features may continue to operate when enabled by the Customer, because those features
            are part of providing the Services to that Customer.
          </p>

          <h2 className="mt-10 text-lg font-semibold">
            5. Customer Instructions and Agent Actions
          </h2>
          <p>
            AI Enterprise Agents may be configured to take actions in connected systems,
            including reading, writing, summarizing, drafting, sending, posting, creating
            tickets, updating records, retrieving documents, querying databases, generating code,
            creating reports, scheduling events, or triggering workflows.
          </p>
          <p>
            Boo Lab processes Customer Content and integration data based on Customer
            configuration, User instructions, product settings, and contractual obligations.
            Customers are responsible for selecting integrations, granting permissions, reviewing
            agent behavior, setting approval controls, and determining whether outputs or actions
            are appropriate for their business.
          </p>

          <h2 className="mt-10 text-lg font-semibold">6. Cookies and Similar Technologies</h2>
          <p>
            We may use cookies, pixels, local storage, and similar technologies to operate the
            website, keep Users signed in, remember preferences, understand website traffic,
            improve the Services, and support security and analytics.
          </p>
          <p>
            Users can control cookies through browser settings. Some features may not work
            properly if cookies are disabled.
          </p>

          <h2 className="mt-10 text-lg font-semibold">7. How We Share Information</h2>
          <p>We may share information with:</p>

          <h3 className="mt-6 font-semibold">Service Providers and Subprocessors</h3>
          <p>
            We may share information with vendors and service providers that help us provide the
            Services, including cloud hosting providers, AI model providers, infrastructure
            providers, security vendors, analytics providers, payment processors, customer
            support tools, email providers, and professional advisors.
          </p>

          <h3 className="mt-6 font-semibold">Customer Selected Integrations</h3>
          <p>
            When Customers connect third party services, we may share information with those
            services as directed by the Customer, including to read, write, update, or transmit
            information through the integration.
          </p>

          <h3 className="mt-6 font-semibold">Business Customers</h3>
          <p>
            If a User accesses the Services through a Customer account, the Customer may access
            information about that User's account, role, activity, Customer Content, agent
            usage, outputs, audit logs, and related information.
          </p>

          <h3 className="mt-6 font-semibold">Legal and Safety Reasons</h3>
          <p>
            We may disclose information if we believe it is reasonably necessary to comply with
            law, legal process, governmental requests, enforce our agreements, protect rights,
            privacy, safety, or property, prevent fraud, investigate security issues, or address
            abuse.
          </p>

          <h3 className="mt-6 font-semibold">Business Transfers</h3>
          <p>
            We may disclose or transfer information in connection with a merger, acquisition,
            financing, reorganization, sale of assets, bankruptcy, or similar transaction
            involving all or part of our business.
          </p>

          <h3 className="mt-6 font-semibold">Aggregated or Deidentified Information</h3>
          <p>
            We may share aggregated, deidentified, anonymized, or statistical information that
            does not identify a Customer, User, or individual.
          </p>

          <h2 className="mt-10 text-lg font-semibold">8. Data Retention</h2>
          <p>
            We retain personal information and Customer Content for as long as reasonably
            necessary to provide the Services, comply with legal obligations, resolve disputes,
            enforce agreements, maintain security, keep business records, and fulfill the
            purposes described in this Privacy Policy.
          </p>
          <p>
            Customers may request deletion of Customer Content as permitted by their agreement
            with Boo Lab. Some information may remain in backups, logs, audit records, security
            records, or legal records for a limited period where retention is necessary.
          </p>

          <h2 className="mt-10 text-lg font-semibold">9. Security</h2>
          <p>
            We use administrative, technical, and organizational safeguards designed to protect
            information against unauthorized access, loss, misuse, alteration, and disclosure.
            These safeguards may include access controls, encryption, logging, monitoring,
            authentication, least privilege permissions, vendor review, and security procedures.
          </p>
          <p>
            No system is completely secure. Customers are responsible for configuring
            integrations, access permissions, approval flows, secrets, credentials, and User
            accounts appropriately.
          </p>

          <h2 className="mt-10 text-lg font-semibold">10. International Data Transfers</h2>
          <p>
            Boo Lab is based in the United States. Information may be processed in the United
            States and other countries where Boo Lab, its service providers, or its
            subprocessors operate.
          </p>
          <p>
            Where required by applicable law, Boo Lab uses appropriate safeguards for
            international transfers of personal information, which may include contractual
            protections or other lawful transfer mechanisms.
          </p>

          <h2 className="mt-10 text-lg font-semibold">11. Privacy Roles</h2>
          <p>
            For Customer Content processed through AI Enterprise Agents, Boo Lab generally acts
            as a service provider, processor, or similar role on behalf of the Customer. The
            Customer generally determines the purposes and means of processing Customer Content.
          </p>
          <p>
            For account information, website information, billing information, business contact
            information, product analytics, security logs, and Boo Lab business operations, Boo
            Lab may act as an independent controller or business.
          </p>

          <h2 className="mt-10 text-lg font-semibold">12. Customer Responsibilities</h2>
          <p>Customers are responsible for:</p>
          <ol className="list-decimal space-y-1 pl-6">
            <li>
              providing required notices and obtaining required rights, consents, and permissions
              for Customer Content;
            </li>
            <li>
              ensuring Customer Content may be lawfully processed through the Services;
            </li>
            <li>configuring integrations and agent permissions appropriately;</li>
            <li>managing User access, approval flows, and administrative controls;</li>
            <li>
              responding to privacy requests relating to Customer Content where the Customer
              controls the data;
            </li>
            <li>
              avoiding submission of sensitive personal information unless necessary and
              permitted by the Customer's agreement with Boo Lab.
            </li>
          </ol>

          <h2 className="mt-10 text-lg font-semibold">13. Privacy Rights</h2>
          <p>
            Depending on location and applicable law, individuals may have rights to request
            access, correction, deletion, portability, restriction, objection, or withdrawal of
            consent relating to personal information.
          </p>
          <p>
            To make a privacy request, contact{" "}
            <a
              href="mailto:privacy@boolab.ai"
              className="underline underline-offset-4 hover:text-foreground"
            >
              privacy@boolab.ai
            </a>
            .
          </p>
          <p>
            If a request relates to Customer Content controlled by a Boo Lab Customer, we may
            refer the request to the relevant Customer or process the request according to the
            Customer's instructions.
          </p>

          <h2 className="mt-10 text-lg font-semibold">14. California Privacy Notice</h2>
          <p>
            This section applies to California residents where the California Consumer Privacy
            Act, as amended, applies.
          </p>
          <p>
            We may collect the following categories of personal information: identifiers,
            business contact information, internet or network activity, geolocation derived from
            IP address, commercial information, professional or employment related information,
            inferences, audio or visual information where calls or recordings are used, and
            sensitive personal information where Customers or Users provide it or where account
            access credentials are processed.
          </p>
          <p>We collect and use these categories for the purposes described in this Privacy Policy.</p>
          <p>
            We do not sell personal information for money. We do not knowingly sell or share
            personal information of individuals under 16. Because Boo Lab is a business only
            service, the Services are not intended for individuals under 18.
          </p>
          <p>
            California residents may have the right to know, access, correct, delete, opt out of
            sale or sharing, limit certain uses of sensitive personal information, and not be
            discriminated against for exercising privacy rights.
          </p>
          <p>
            To exercise rights, contact{" "}
            <a
              href="mailto:privacy@boolab.ai"
              className="underline underline-offset-4 hover:text-foreground"
            >
              privacy@boolab.ai
            </a>
            .
          </p>
          <p>
            Authorized agents may submit requests on behalf of California residents where
            permitted by law. We may need to verify the request before acting on it.
          </p>

          <h2 className="mt-10 text-lg font-semibold">
            15. European Economic Area, United Kingdom, and Switzerland
          </h2>
          <p>
            Where GDPR, UK GDPR, or similar laws apply, the legal bases for processing may
            include performance of a contract, legitimate interests, compliance with legal
            obligations, consent, and processing necessary to protect rights, security, or
            safety.
          </p>
          <p>
            Individuals may have rights to access, rectify, erase, restrict, object to
            processing, request portability, withdraw consent, and lodge a complaint with a
            supervisory authority.
          </p>
          <p>
            For Customer Content, Customers are generally responsible for responding to requests
            from individuals, and Boo Lab will assist as required by applicable agreements and
            law.
          </p>

          <h2 className="mt-10 text-lg font-semibold">16. Children</h2>
          <p>
            The Services are intended for business use by individuals who are at least 18 years
            old. We do not knowingly collect personal information from children.
          </p>

          <h2 className="mt-10 text-lg font-semibold">17. Third Party Services</h2>
          <p>
            The Services may connect to third party platforms and services. Those third parties
            process information according to their own terms and privacy policies. Customers are
            responsible for reviewing and approving third party integrations before enabling
            them.
          </p>

          <h2 className="mt-10 text-lg font-semibold">18. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. If we make material changes, we
            will provide notice as required by law, which may include posting an updated version
            at https://boolab.ai or notifying Customers through the Services or by email.
          </p>

          <h2 className="mt-10 text-lg font-semibold">19. Contact</h2>
          <p>
            Boo Lab, Inc.
            <br />
            1515 E Cesar Chavez St, Suite 100
            <br />
            Austin, TX 78702
            <br />
            USA
          </p>
          <p>
            Privacy contact:{" "}
            <a
              href="mailto:privacy@boolab.ai"
              className="underline underline-offset-4 hover:text-foreground"
            >
              privacy@boolab.ai
            </a>
            <br />
            Legal notices:{" "}
            <a
              href="mailto:legal@boolab.ai"
              className="underline underline-offset-4 hover:text-foreground"
            >
              legal@boolab.ai
            </a>
          </p>
        </div>

        <footer className="mt-12 flex gap-4 border-t pt-6 text-sm text-muted-foreground">
          <Link
            to="/terms-and-conditions"
            className="underline underline-offset-4 hover:text-foreground"
          >
            Terms and Conditions
          </Link>
          <Link to="/login" className="underline underline-offset-4 hover:text-foreground">
            Sign in
          </Link>
        </footer>
      </div>
    </div>
  );
}
